/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// UNIFIED SAVED VIEWS HOOK - Single Public API for All Saved Views
// ============================================================================
//
// This is the SINGLE PUBLIC API for saved views across the application.
// It automatically selects the appropriate storage backend (server vs local)
// based on viewType and normalizes all views to use `id` as the primary key.
//
// **Storage Backend Selection:**
// - Server-backed: projects, clients, contacts, estimates, proposals
//   → Uses useUnifiedSavedViews (server API with saved_views table)
// - Local storage: suppliers, cost-profiles, services, documents
//   → Uses useLocalSavedViewsAdapter (browser localStorage)
//
// **Normalization:**
// - All views are normalized to NormalizedSavedView with `id` key
// - Server-backed views map `view_id` → `id` automatically
// - Local storage views already use `id`
//
// **Migration Guide:**
// - Replace useUnifiedSavedViews → useSavedViewsUnified (same API)
// - Replace useServerSavedViews → useSavedViewsUnified (normalizes view_id → id)
// - Replace useLocalSavedViewsAdapter → useSavedViewsUnified (add storageKey)
//
// ============================================================================

"use client";

import { logger } from "@/lib/utils/logger";
import type { NormalizedSavedView } from "@/types/saved-views";
import type { SavedViewType } from "./";
import { useLocalSavedViewsAdapter } from "./internal/use-local-saved-views-adapter";
import { useUnifiedSavedViewsInternal } from "./use-unified-saved-views.internal";

/**
 * Supported server-backed view types
 * These view types have server-side storage in the saved_views table.
 * They use useUnifiedSavedViews internally which handles API routing.
 */
const SERVER_BACKED_VIEW_TYPES: readonly SavedViewType[] = [
  "projects",
  "clients",
  "suppliers",
] as const;

/**
 * Known local storage view types
 * These view types use local storage via useLocalSavedViewsAdapter.
 * Requires storageKey parameter for localStorage key.
 *
 * Note: "pipeline" is stored locally to keep it separate from "clients" saved views,
 * preventing collisions between client list views and pipeline board views.
 */
const LOCAL_STORAGE_VIEW_TYPES: readonly SavedViewType[] = [
  "contacts",
  "estimates",
  "proposals",
  "cost-profiles",
  "services",
  "documents",
  "materials",
  "equipment",
  "labor-rates",
  "pipeline",
  "invoices",
  "subcontractors",
  "audit-logs",
] as const;

/**
 * Safe default return value for unsupported view types
 */
function createSafeDefault<TFilters extends Record<string, unknown>>() {
  return {
    views: [] as NormalizedSavedView<TFilters>[],
    selectedViewId: undefined as string | undefined,
    selectedView: undefined as NormalizedSavedView<TFilters> | undefined,
    isDirty: false,
    isLoading: false,
    error: null as string | null,
    handleApplyView: () => {
      logger.warn("handleApplyView called on unsupported view type");
    },
    handleSaveView: async () => {
      logger.warn("handleSaveView called on unsupported view type");
    },
    handleDeleteView: async () => {
      logger.warn("handleDeleteView called on unsupported view type");
    },
  };
}

/**
 * Determines which adapter to use and validates configuration
 * This runs before hook calls to make adapter selection predictable
 *
 * Validates view type support and storageKey requirements upfront,
 * preventing runtime errors by identifying invalid configurations early.
 */
function determineAdapterConfig(
  viewType: SavedViewType,
  storageKey?: string,
): {
  adapterType: "server" | "local" | "unsupported";
  isValid: boolean;
  reason?: string;
} {
  const isServerBacked = SERVER_BACKED_VIEW_TYPES.includes(
    viewType as SavedViewType,
  );
  const isLocalStorage = LOCAL_STORAGE_VIEW_TYPES.includes(
    viewType as SavedViewType,
  );

  // Unsupported view type
  if (!isServerBacked && !isLocalStorage) {
    return {
      adapterType: "unsupported",
      isValid: false,
      reason: `View type "${viewType}" is not supported. Supported server-backed: ${SERVER_BACKED_VIEW_TYPES.join(", ")}, Supported local: ${LOCAL_STORAGE_VIEW_TYPES.join(", ")}`,
    };
  }

  // Server-backed - no storageKey needed
  if (isServerBacked) {
    return {
      adapterType: "server",
      isValid: true,
    };
  }

  // Local storage - storageKey is required
  if (isLocalStorage) {
    if (!storageKey) {
      return {
        adapterType: "local",
        isValid: false,
        reason: `storageKey is required for local storage view type "${viewType}"`,
      };
    }
    return {
      adapterType: "local",
      isValid: true,
    };
  }

  // Fallback (should never reach here)
  return {
    adapterType: "unsupported",
    isValid: false,
    reason: `Unable to determine adapter for view type "${viewType}"`,
  };
}

/**
 * Unified saved views hook - SINGLE PUBLIC API for all saved views
 *
 * This hook automatically selects the appropriate storage backend based on viewType
 * and provides a consistent normalized interface. All views are normalized to use
 * `id` as the primary identifier.
 *
 * **Storage Backend Selection:**
 * - Server-backed (projects, clients, contacts, estimates): Uses server API with saved_views table
 * - Local storage (suppliers, cost-profiles, services, documents): Uses browser localStorage
 *
 * **Normalization:**
 * - All views returned use `id` as the primary key (never `view_id`)
 * - Server-backed views are automatically normalized from `view_id` → `id`
 * - Local storage views already use `id`
 *
 * **Graceful Degradation:**
 * - Returns safe defaults for unsupported view types (prevents runtime crashes)
 * - Validates configuration before hook invocation
 * - Logs warnings/errors for invalid configurations
 *
 * **Migration from Other Hooks:**
 * - `useUnifiedSavedViews` → `useSavedViewsUnified` (same API, just rename)
 * - `useServerSavedViews` → `useSavedViewsUnified` (normalizes view_id → id automatically)
 * - `useLocalSavedViewsAdapter` → `useSavedViewsUnified` (add storageKey parameter)
 *
 * @example Server-backed view (projects, clients, contacts, estimates)
 * ```tsx
 * const {
 *   views,              // NormalizedSavedView[] - always uses 'id' field
 *   selectedViewId,     // string | undefined
 *   selectedView,       // NormalizedSavedView | undefined
 *   isDirty,            // boolean
 *   handleApplyView,    // (viewId: string | null) => void
 *   handleSaveView,     // (name?: string) => Promise<void>
 *   handleDeleteView,   // () => Promise<void>
 *   isLoading,          // boolean
 *   error,              // string | null
 * } = useSavedViewsUnified({
 *   viewType: "projects",
 *   currentFilters: myFilters,
 *   onApplyView: (filters) => setFilters(filters),
 *   defaultFilters: {},
 * });
 * ```
 *
 * @example Local storage view (suppliers, services, documents, cost-profiles)
 * ```tsx
 * const {
 *   views,
 *   selectedViewId,
 *   selectedView,
 *   isDirty,
 *   handleApplyView,
 *   handleSaveView,
 *   handleDeleteView,
 * } = useSavedViewsUnified({
 *   viewType: "suppliers",
 *   storageKey: "suppliers", // Required for local storage types
 *   currentFilters: myFilters,
 *   onApplyView: (filters) => setFilters(filters),
 *   defaultFilters: {},
 * });
 * ```
 */
export function useSavedViewsUnified<
  TFilters extends Record<string, unknown>,
>(options: {
  /**
   * View type identifier (e.g., "projects", "clients", "suppliers")
   */
  viewType: SavedViewType;

  /**
   * Storage key for local storage view types (required for non-server-backed types)
   */
  storageKey?: string;

  /**
   * Current filter state for comparison (determines dirty state)
   */
  currentFilters?: TFilters;

  /**
   * Callback to apply filters from a saved view
   */
  onApplyView?: (filters: TFilters) => void;

  /**
   * Whether to sync selected view ID with URL (default: true)
   */
  syncWithUrl?: boolean;

  /**
   * Whether to auto-apply default view on mount (default: false)
   */
  autoApplyDefault?: boolean;

  /**
   * Whether to include organization presets (default: true, server-backed only)
   */
  includeOrgPresets?: boolean;

  /**
   * Whether the hook is enabled (default: true)
   */
  enabled?: boolean;

  /**
   * Default/reset filters to use when clearing a view (handleApplyView(null))
   * If not provided, an empty object {} will be used
   */
  defaultFilters?: TFilters;
}) {
  const { viewType, storageKey, ...restOptions } = options;

  // Determine adapter configuration BEFORE hook calls
  // This makes adapter selection predictable and validates early
  const adapterConfig = determineAdapterConfig(viewType, storageKey);

  // Log warnings/errors for invalid configurations (but don't throw)
  if (!adapterConfig.isValid) {
    if (adapterConfig.adapterType === "unsupported") {
      logger.warn(
        `Unsupported view type "${viewType}". Returning safe defaults.`,
        {
          viewType,
          reason: adapterConfig.reason,
          supportedServerBacked: SERVER_BACKED_VIEW_TYPES,
          supportedLocalStorage: LOCAL_STORAGE_VIEW_TYPES,
        },
      );
    } else {
      logger.error(
        `Invalid configuration for view type "${viewType}". Returning safe defaults.`,
        {
          viewType,
          storageKey,
          reason: adapterConfig.reason,
        },
      );
    }
  }

  // Determine which hooks to enable based on adapter config
  // Hooks must be called unconditionally (React rules), but we disable them appropriately
  const hookEnabled = restOptions.enabled !== false && adapterConfig.isValid;
  const enableServerHook =
    hookEnabled && adapterConfig.adapterType === "server";
  const enableLocalHook = hookEnabled && adapterConfig.adapterType === "local";

  // Call server-backed hook (disabled if not using server adapter)
  const serverResult = useUnifiedSavedViewsInternal<TFilters>({
    viewType,
    ...restOptions,
    enabled: enableServerHook,
  });

  // Call local storage hook (disabled if not using local adapter)
  // Provide a default storageKey to satisfy TypeScript, but it won't be used if disabled
  const localResult = useLocalSavedViewsAdapter<TFilters>({
    viewType,
    storageKey: storageKey ?? `default-${viewType}`,
    ...restOptions,
    enabled: enableLocalHook,
  });

  // If configuration is invalid, return safe defaults immediately
  // This prevents any runtime errors while satisfying React Hooks rules
  if (!adapterConfig.isValid) {
    return createSafeDefault<TFilters>();
  }

  // Return the appropriate result based on adapter configuration
  // At this point we know the configuration is valid
  if (adapterConfig.adapterType === "server") {
    return {
      ...serverResult,
      views: serverResult.views as NormalizedSavedView<TFilters>[],
    };
  }

  // Must be local storage adapter (we validated earlier)
  return {
    ...localResult,
    views: localResult.views as NormalizedSavedView<TFilters>[],
  };
}
